import { create } from 'zustand';
import api from '@/services/api';
import { asApiError } from '@/lib/apiError';

interface SystemSettings {
  environment_type: 'development' | 'testing' | 'production';
  decimal_format: 'dot' | 'comma';
  date_format: 'DD.MM.YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY';
  is_maintenance_mode: boolean;
  is_registration_open: boolean;
  allow_org_member_invites: boolean;
  server_name: string;
  social_login_enabled: boolean;
  supported_languages: string[];
  // T3152: domača stran spletne strani tega okolja (prazno = brez povezave).
  website_url?: string;
  // T982: spodnja polja vrača samo admin endpoint (`sysadmin/settings/`),
  // javni `config/` jih ne razkriva več.
  updated_at?: string;
  trial_check_time?: string;       // "HH:MM:SS" format from Django TimeField
  metrics_snapshot_time?: string;
  daily_digest_time?: string;
  weekly_digest_time?: string;
  weekly_digest_day?: number;      // 0=Monday, 6=Sunday
  cleanup_time?: string;
}

interface PermissionCategory {
  [key: string]: string[];
}

interface SystemPermissions {
  permissions: string[];
  categories: PermissionCategory;
  role_defaults: { [key: string]: string[] };
}

interface SystemState {
  settings: SystemSettings | null;
  availablePermissions: SystemPermissions | null;
  isLoading: boolean;
  error: string | null;
  fetchSettings: () => Promise<void>;
  fetchAdminSettings: () => Promise<void>;
  fetchPermissions: () => Promise<void>;
  updateSettings: (newSettings: Partial<SystemSettings>) => Promise<void>;
}

export const useSystemStore = create<SystemState>((set, get) => ({
  settings: null,
  availablePermissions: null,
  isLoading: false,
  error: null,

  fetchSettings: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get('config/');
      set({ settings: response.data, isLoading: false });
    } catch (err) {
      set({ error: asApiError(err).message, isLoading: false });
    }
  },

  // T982: javni `config/` ne vrača več urnikov opravil — sysadmin UI mora
  // polne nastavitve brati z admin endpointa.
  fetchAdminSettings: async () => {
    const response = await api.get('sysadmin/settings/');
    set({ settings: response.data });
  },

  fetchPermissions: async () => {
    // Only fetch if not already loaded
    if (get().availablePermissions) return;

    try {
      const response = await api.get('permissions/');
      set({ availablePermissions: response.data });
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      // T974: rethrow, da klicatelj lahko prikaže error stanje namesto
      // večnega spinnerja (availablePermissions ostane null).
      throw err;
    }
  },

  updateSettings: async (newSettings) => {
    try {
      const response = await api.patch('sysadmin/settings/', newSettings);
      set({ settings: response.data });
    } catch (err) {
      console.error('Failed to update system settings:', err);
      throw err;
    }
  },
}));
